// Commands panel (device page): send a command to this tracker through our gateway and see the
// answers. Presets are read-only and open to all staff; anything else is admin only and asks for
// confirmation showing the exact command and tracker; restarts and server settings ask twice.
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { ApiError, CommandRow, commands } from '../api';
import { useAuth } from '../auth';
import { ago, dateTime } from '../format';

const STATUS: Record<CommandRow['status'], string> = {
  queued: 'Waiting for the tracker', sent: 'Sent, waiting for the answer', answered: 'Answered', failed: 'Failed', expired: 'Expired', cancelled: 'Cancelled',
};

export function Commands({ trackerId, imei, label }: { trackerId: number; imei: string; label: string }) {
  const { user } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof commands.list>> | null>(null);
  const [error, setError] = useState('');
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const admin = user?.role === 'admin';

  const load = useCallback(() => {
    commands.list(trackerId).then((d) => { setData(d); setError(''); }).catch((e) => setError((e as Error).message));
  }, [trackerId]);
  useEffect(load, [load]);
  // While something is on its way, look again every 5 seconds.
  const waiting = data?.history.some((c) => c.status === 'queued' || c.status === 'sent');
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [waiting, load]);

  const send = async (text: string, preset: boolean) => {
    setBusy(true);
    setError('');
    try {
      let confirmed = false;
      if (!preset) {
        if (!window.confirm(`Send this command to ${label} (IMEI ${imei})?\n\n${text}`)) { setBusy(false); return; }
        confirmed = true;
      }
      try {
        await commands.send(trackerId, { command: text, confirmed });
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'confirm-server') throw err;
        // A restart or a server setting: ask a second time, saying why.
        if (!window.confirm(`Second confirmation – ${err.message}\n\nA wrong server setting cuts the tracker off, and on trackers that also report to Mapon it can break that feed.\n\nSend "${text}" to IMEI ${imei}?`)) { setBusy(false); return; }
        await commands.send(trackerId, { command: text, confirmed: true, serverConfirmed: true });
      }
      if (!preset) setCustom('');
      load();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };
  const submit = (e: FormEvent) => { e.preventDefault(); if (custom.trim()) void send(custom.trim(), false); };
  const cancel = async (c: CommandRow) => {
    try { await commands.cancel(c.id); load(); } catch (err) { setError((err as Error).message); }
  };

  return (
    <section className="card form cmd-box" aria-label="Commands">
      <div className="page-head"><h2>Commands</h2></div>
      {!data && !error && <p className="muted">Loading…</p>}
      {data && !data.available && (
        <p className="notice small">
          Commands are sent by our own gateway once it takes over from Traccar (gateway stage 2). Until then nothing can be sent from here.
        </p>
      )}
      {data && (
        <>
          <div className="cmd-presets" role="group" aria-label="Preset commands">
            {Object.entries(data.presets).map(([cmd, what]) => (
              <button key={cmd} type="button" className="button ghost small" title={what} disabled={busy || !data.available} onClick={() => void send(cmd, true)}>
                {cmd}
              </button>
            ))}
          </div>
          {admin && (
            <form className="cmd-custom" onSubmit={submit}>
              <label className="sr-only" htmlFor={`cmd-${trackerId}`}>Custom command</label>
              <input id={`cmd-${trackerId}`} type="text" maxLength={200} value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom command, e.g. getparam 1000" disabled={!data.available} className="mono" />
              <button type="submit" className="primary small" disabled={busy || !custom.trim() || !data.available}>Send…</button>
            </form>
          )}
          <p className="muted small">
            Sent only to this tracker ({imei}); every command is logged with who sent it.
            {admin ? ' Restarts and server settings (2004–2010) ask for a second confirmation.' : ' Admins can send other commands.'}
          </p>
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      {data && data.history.length > 0 && (
        <ol className="cmd-history">
          {data.history.map((c) => (
            <li key={c.id} className={`cmd cmd-${c.status}`}>
              <div className="cmd-top">
                <span className="mono">{c.command}</span>
                <span className={`cmd-status cmd-st-${c.status}`}>{STATUS[c.status]}</span>
              </div>
              {c.response && <pre className="cmd-answer">{c.response}</pre>}
              {c.error && <div className="small danger">{c.error}</div>}
              <div className="muted small">
                {c.by ?? 'someone'} · <time dateTime={c.createdAt ?? undefined} title={c.createdAt ? dateTime(c.createdAt) : undefined}>{ago(c.createdAt)}</time>
                {c.answeredAt && <> · answered {dateTime(c.answeredAt)}</>}
                {c.status === 'queued' && <> · <button type="button" className="link small" onClick={() => void cancel(c)}>Cancel</button></>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
